import React from 'react'
import classNames from 'classnames'
import RoomIcon from '@material-ui/icons/Room'
import PhoneIcon from '@material-ui/icons/Phone'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import AddIcon from '@material-ui/icons/Add'
import JobAddActionMenu from './JobAddActionMenu'
import type { PainterScheduleEvent } from './types'
import type { usePainterJobCardStyles } from './usePainterJobCardStyles'
import { getPainterEventAddress } from './eventUtils'
import { ClickAwayListener, Popper } from '@paintscout/ui'

interface JobActionRowProps {
  event: PainterScheduleEvent
  showContactInfo: boolean
  showTasksNotesFiles: boolean
  showPayments: boolean
  paymentsDisabled?: boolean
  canAddAdditionalWork: boolean
  onDirections?: (event: PainterScheduleEvent) => void
  onCall?: (event: PainterScheduleEvent) => void
  onMessage?: (event: PainterScheduleEvent) => void
  onAddAdditionalWork: () => void
  onAddTask: () => void
  onAddNote: () => void
  onAddFile: () => void
  onAddPayment: () => void
  classes: ReturnType<typeof usePainterJobCardStyles>
}

export default function JobActionRow(props: JobActionRowProps) {
  const {
    event,
    showContactInfo,
    showTasksNotesFiles,
    showPayments,
    paymentsDisabled,
    canAddAdditionalWork,
    onDirections,
    onCall,
    onMessage,
    onAddAdditionalWork,
    onAddTask,
    onAddNote,
    onAddFile,
    onAddPayment,
    classes
  } = props
  const [menuOpen, setMenuOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLButtonElement>(null)
  const canOpenDirections = !!getPainterEventAddress(event)
  const canCall = showContactInfo && !!event.contact?.phone
  const canMessage = showContactInfo && !!event.contact?.phone

  return (
    <div className={classes.actionRow}>
      <button
        type="button"
        className={classNames(classes.actionBtn, !canOpenDirections && classes.actionBtnDisabled)}
        onClick={() => canOpenDirections && onDirections?.(event)}
        aria-label="directions"
        disabled={!canOpenDirections}
      >
        <RoomIcon className={classes.actionBtnIcon} />
      </button>
      <button
        type="button"
        className={classNames(classes.actionBtn, !canCall && classes.actionBtnDisabled)}
        onClick={() => canCall && onCall?.(event)}
        aria-label="call"
        disabled={!canCall}
      >
        <PhoneIcon className={classes.actionBtnIcon} />
      </button>
      <button
        type="button"
        className={classNames(classes.actionBtn, !canMessage && classes.actionBtnDisabled)}
        onClick={() => canMessage && onMessage?.(event)}
        aria-label="message"
        disabled={!canMessage}
      >
        <ChatBubbleOutlineIcon className={classes.actionBtnIcon} />
      </button>
      {(showTasksNotesFiles || showPayments || canAddAdditionalWork) && (
        <div className={classes.addActionAnchor}>
          <button
            ref={anchorRef}
            type="button"
            className={classes.actionBtn}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="add"
          >
            <AddIcon className={classes.actionBtnIcon} />
          </button>
          <Popper
            open={menuOpen}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            modifiers={{ flip: { enabled: true }, offset: { offset: '0, 6' } }}
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
              <div>
                <JobAddActionMenu
                  canAddAdditionalWork={canAddAdditionalWork}
                  showTasksNotesFiles={showTasksNotesFiles}
                  showPayments={showPayments}
                  paymentsDisabled={paymentsDisabled}
                  onAddAdditionalWork={() => {
                    setMenuOpen(false)
                    onAddAdditionalWork()
                  }}
                  onAddPayment={() => {
                    setMenuOpen(false)
                    onAddPayment()
                  }}
                  onAddFile={() => {
                    setMenuOpen(false)
                    onAddFile()
                  }}
                  onAddTask={() => {
                    setMenuOpen(false)
                    onAddTask()
                  }}
                  onAddNote={() => {
                    setMenuOpen(false)
                    onAddNote()
                  }}
                  classes={classes}
                />
              </div>
            </ClickAwayListener>
          </Popper>
        </div>
      )}
    </div>
  )
}
